import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { apiPost } from '../api/client';
import { useAuth } from '../hooks/useAuth';

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

export default function VerifyEmailRequiredPage() {
    const navigate = useNavigate();
    const { user, initialEmailFailed, loading, logout, refresh } = useAuth();
    const [resendState, setResendState] = useState<ResendState>('idle');
    const [resendError, setResendError] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);

    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.emailVerified) return <Navigate to="/available-badges" replace />;

    const handleResend = async () => {
        setResendState('sending');
        setResendError(null);
        const res = await apiPost<void>('/auth/resend-verification', {});
        if (res.success) {
            setResendState('sent');
            await refresh();
        } else {
            setResendState('error');
            setResendError(res.message ?? 'Could not resend the verification email.');
        }
    };

    const handleCheckAgain = async () => {
        setChecking(true);
        await refresh();
        setChecking(false);
    };

    const handleSignOut = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 460, border: 'none' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <MarkEmailUnreadIcon sx={{ fontSize: 56, color: 'accent.main', mb: 1 }} />
                    <Typography variant="h5" fontWeight={700} mb={1}>Verify your email</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        We sent a verification link to <b>{user.email}</b>. Click the link in that email to continue.
                        The link expires in 24 hours.
                    </Typography>

                    {initialEmailFailed && resendState === 'idle' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            We couldn&apos;t deliver your verification email. Try Resend below, or double-check the address.
                        </Alert>
                    )}
                    {resendState === 'sent' && (
                        <Alert severity="success" sx={{ mb: 2 }}>Verification email sent. Check your inbox.</Alert>
                    )}
                    {resendState === 'error' && resendError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{resendError}</Alert>
                    )}

                    <Box display="flex" flexDirection="column" gap={1.5}>
                        <Button
                            variant="contained"
                            onClick={handleResend}
                            disabled={resendState === 'sending'}
                            fullWidth
                        >
                            {resendState === 'sending' && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
                            {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={handleCheckAgain}
                            disabled={checking}
                            fullWidth
                        >
                            {checking && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
                            I&apos;ve verified — refresh
                        </Button>
                        <Button variant="text" onClick={handleSignOut} size="small">
                            Sign in with a different account
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
